import { type TILE_SIZES } from '@mixtiles/web-backend-shared'
import {
  UPLOADED_LANDSCAPE_SIZE,
  UPLOADED_PORTRAIT_SIZE,
  fetchCollageLayouts,
} from 'services/Collage/layoutFetcher'
import { type Tile } from 'components/TilesProvider/TilesProvider.types'
import {
  getPhotoCroppingParams,
  initCollage,
} from 'services/Collage/collageService'
import orderBy from 'lodash/orderBy'
import { getThumbCroppedImageUrl } from 'components/PhotoBank/tileUtils'
import { preloadImagePromise } from 'utils/ImageUtils'
import { logger } from 'services/logger'

// Configuration defining tile counts and their corresponding sizes
const TILE_CONFIGURATIONS = [
  { photosCount: 2, layoutSize: UPLOADED_LANDSCAPE_SIZE },
  { photosCount: 4, layoutSize: UPLOADED_PORTRAIT_SIZE },
  { photosCount: 9, layoutSize: UPLOADED_PORTRAIT_SIZE },
  { photosCount: 12, layoutSize: UPLOADED_PORTRAIT_SIZE },
  { photosCount: 16, layoutSize: UPLOADED_PORTRAIT_SIZE },
].sort((a, b) => a.photosCount - b.photosCount)

const findNearestConfiguration = (
  photosCount: number
): { photosCount: number; layoutSize: TILE_SIZES } => {
  const availableConfigs = TILE_CONFIGURATIONS.filter(
    (c) => c.photosCount <= photosCount
  )

  return availableConfigs.length > 0
    ? availableConfigs[availableConfigs.length - 1]
    : TILE_CONFIGURATIONS[0]
}

export async function prepareUpsellCollage(tiles: Tile[]) {
  // Order tiles with faces first
  const orderedTiles = orderBy(
    tiles,
    [(tile) => ((tile.faces?.length ?? 0) > 0 ? 1 : 0)],
    ['desc']
  )

  const { photosCount, layoutSize } = findNearestConfiguration(
    orderedTiles.length
  )
  const selectedPhotos = orderedTiles.slice(0, photosCount)
  const layouts = await fetchCollageLayouts(photosCount, {
    tileSize: layoutSize,
  })
  const layout = layouts.grid

  const collage = await initCollage({
    layout,
    collagePhotos: selectedPhotos,
    collageText: {
      showText: true,
      text: 'Precious Moments',
      textStyleId: 'handwriting',
    },
  })

  try {
    await preloadCollagePhotos(Object.values(collage.photos), selectedPhotos)
  } catch (error) {
    logger.warning('Failed to preload upsell collage photos', { error })
  }

  return collage
}

export async function preloadCollagePhotos(
  collagePhotos: any[],
  tiles: Tile[]
) {
  await Promise.all(
    collagePhotos.map(async (photo) => {
      const photoBankPhoto = tiles.find((p) => p.uid === photo.uid)
      if (!photoBankPhoto) {
        return
      }
      const { cropParams } = getPhotoCroppingParams(photo)
      const croppedImageUrl = await getThumbCroppedImageUrl(
        photoBankPhoto || {},
        cropParams
      )
      await preloadImagePromise(croppedImageUrl)
    })
  )
}
